import { debounce } from 'lodash-es';
import { useEffect, useMemo, useRef } from 'react';

type DebounceSettings = Parameters<typeof debounce>[2];

/**
 * A debounced callback for React
 */
export function useDebounceCallback<Args extends unknown[], R>(
  callback: (...args: Args) => R,
  delay: number,
  options?: DebounceSettings,
) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const debouncedCallback = useMemo(() => {
    return debounce((...args: Args) => callbackRef.current(...args), delay, options);
  }, [delay, options]);

  // Cleanup to cancel debounce on unmount
  useEffect(() => {
    return () => debouncedCallback.cancel();
  }, [debouncedCallback]);

  // A `DebouncedFunc` takes the wrapped callback's arguments, but returns `undefined`
  // when a call is deferred (and also exposes `cancel`/`flush`).
  return debouncedCallback;
}
